import React from "react";
import { Col, Row } from "reactstrap";
import * as Yup from "yup";
import * as moment from "moment";

import {
  addPersonRecord,
  editPersonRecord,
  getClassifiedInformationTypes,
  PersonRecordKind,
  removePersonRecord,
} from "common/services/persons.service";
import { normalize } from "common/lib/normalize";
import {
  AsyncSelectField,
  DateField,
  Option,
  OptionsSelectField,
  SwitchField,
  TextField,
} from "Components/Common/RecordManager/fields";
import {
  agencyCache,
  endAfterStart,
  initials,
  joinMeta,
  loadAgencies,
  loadContractors,
  loadPersons,
  option,
  period,
  toApiDate,
  toDate,
  useReferenceOptions,
} from "Components/Common/RecordManager/helpers";
import {
  FieldsProps,
  RecordKindConfig,
  RecordStore,
} from "Components/Common/RecordManager/types";

import { SEMG_MENTIONS as CONTRACTOR_SEMG_MENTIONS } from "pages/Contractors/ViewContractors/recordKinds";

/** The person profile's record kinds for the shared record manager. */

const personRecordStore = (kind: PersonRecordKind): RecordStore => ({
  add: async (person, payload) => {
    const response: any = await addPersonRecord(person.id, kind, payload);
    return response?.data?.id ?? null;
  },
  edit: async (person, record, payload) => {
    await editPersonRecord(person.id, kind, record.id, payload);
  },
  remove: async (person, record) => {
    await removePersonRecord(person.id, kind, record.id);
  },
});

/** Permission actions on the person API's profile-record controller. */
const profileRecordPermissions = (add: string, edit: string, remove: string) => ({
  resource: "person",
  controller: "profile-record",
  add,
  edit,
  remove,
});

const formatDay = (value: any) => (value ? moment(value).format("D MMM YYYY") : "");

// ── classified information ───────────────────────────────────────────────────

const classifiedTypeCache: { current: Option[] | null } = { current: null };
const loadClassifiedTypes = async () => {
  const response: any = await getClassifiedInformationTypes();
  return normalize(response.data).map((type: any) => ({ value: type.id, label: type.name }));
};

const ClassifiedInformationFields = ({ form, readOnly }: FieldsProps) => {
  const types = useReferenceOptions(loadClassifiedTypes, classifiedTypeCache);
  const agencies = useReferenceOptions(loadAgencies, agencyCache);
  return (
    <>
      <div className="record-manager__notice is-warning mb-3" role="note">
        <i className="ri-shield-keyhole-line" aria-hidden="true"></i>
        <span>
          <strong>Confidential</strong> – shown only to roles allowed to see classified
          information. Contact the RMU about access.
        </span>
      </div>
      <Row>
        <Col md={6}>
          <OptionsSelectField
            form={form}
            name="type"
            label="Type of information"
            required
            readOnly={readOnly}
            placeholder="Select a type"
            options={types.options}
            isLoading={types.loading}
          />
        </Col>
        <Col md={6}>
          <OptionsSelectField
            form={form}
            name="reportingAgency"
            label="Reporting agency"
            required
            readOnly={readOnly}
            placeholder="Select an agency"
            options={agencies.options}
            isLoading={agencies.loading}
          />
        </Col>
        <Col md={6}>
          <DateField form={form} name="date" label="Date" required readOnly={readOnly} />
        </Col>
        <Col md={12}>
          <TextField
            form={form}
            name="description"
            label="Description or reason"
            multiline
            readOnly={readOnly}
            placeholder="What was reported and why it matters"
          />
        </Col>
      </Row>
    </>
  );
};

export const CLASSIFIED_INFORMATION: RecordKindConfig = {
  kind: "classified-information",
  title: "Classified information",
  noun: "entry",
  permissions: profileRecordPermissions(
    "addClassifiedInformation",
    "editClassifiedInformation",
    "removeClassifiedInformation"
  ),
  store: personRecordStore("classified-information"),
  records: (person) =>
    [...(person?.classifiedInformation || [])].sort(
      (a, b) => new Date(b?.date || 0).getTime() - new Date(a?.date || 0).getTime()
    ),
  summary: (record) => ({
    title: record?.type?.name || "Untyped entry",
    meta: joinMeta(record?.reportingAgency?.name, formatDay(record?.date)),
    avatar: { icon: "ri-shield-keyhole-line", tone: "warning" },
  }),
  emptyValues: { type: null, reportingAgency: null, date: null, description: "" },
  toValues: (record) => ({
    type: option(record?.type?.id, record?.type?.name),
    reportingAgency: option(record?.reportingAgency?.id, record?.reportingAgency?.name),
    date: toDate(record?.date),
    description: record?.description || "",
  }),
  toPayload: (values) => ({
    type: values.type?.value ?? null,
    reportingAgency: values.reportingAgency?.value ?? null,
    date: toApiDate(values.date),
    description: values.description,
  }),
  schema: Yup.object({
    type: Yup.object().nullable().required("Choose the type of information"),
    reportingAgency: Yup.object().nullable().required("Choose the reporting agency"),
    date: Yup.date().nullable().required("Choose the date"),
  }),
  Fields: ClassifiedInformationFields,
  removeNote: () => "This deletes the entry from the person's profile.",
};

// ── work history ─────────────────────────────────────────────────────────────

const WorkHistoryFields = ({ form, readOnly }: FieldsProps) => (
  <Row>
    <Col md={12}>
      <AsyncSelectField
        form={form}
        name="contractor"
        label="Contractor"
        required
        readOnly={readOnly}
        placeholder="Search contractors by name"
        loadOptions={loadContractors()}
        hint="The entry also appears on the contractor's staff and board list."
      />
    </Col>
    <Col md={12}>
      <TextField
        form={form}
        name="role"
        label="Role"
        readOnly={readOnly}
        placeholder="For example Director, Finance officer"
      />
    </Col>
    <Col md={6}>
      <DateField form={form} name="fromDate" label="From" readOnly={readOnly} />
    </Col>
    <Col md={6}>
      <DateField
        form={form}
        name="toDate"
        label="To"
        readOnly={readOnly}
        hint="Leave empty while they are still in the role."
      />
    </Col>
  </Row>
);

export const WORK_HISTORY: RecordKindConfig = {
  kind: "work-history",
  title: "Work history",
  noun: "position",
  permissions: profileRecordPermissions("addWorkHistory", "editWorkHistory", "removeWorkHistory"),
  store: personRecordStore("work-history"),
  records: (person) =>
    [...(person?.boardOfDirectors || [])].sort(
      (a, b) =>
        new Date(b?.fromDate || 0).getTime() - new Date(a?.fromDate || 0).getTime()
    ),
  summary: (record) => ({
    title: record?.contractor?.contractorName || "Unknown contractor",
    meta: joinMeta(record?.role, period(record?.fromDate, record?.toDate)),
    avatar: { icon: "ri-building-line", tone: "primary" },
  }),
  profileLink: (record) =>
    record?.contractor?.id
      ? { to: `/contractors/${record.contractor.id}`, label: "Open contractor" }
      : null,
  emptyValues: { contractor: null, role: "", fromDate: null, toDate: null },
  toValues: (record) => ({
    contractor: option(record?.contractor?.id, record?.contractor?.contractorName),
    role: record?.role || "",
    fromDate: toDate(record?.fromDate),
    toDate: toDate(record?.toDate),
  }),
  toPayload: (values) => ({
    contractor: values.contractor?.value ?? null,
    role: values.role,
    fromDate: toApiDate(values.fromDate),
    toDate: toApiDate(values.toDate),
  }),
  schema: Yup.object({
    contractor: Yup.object().nullable().required("Choose a contractor"),
    fromDate: Yup.date().nullable(),
    toDate: endAfterStart("fromDate"),
  }),
  Fields: WorkHistoryFields,
  removeNote: (record) =>
    `This removes the position here and from ${
      record?.contractor?.contractorName || "the contractor"
    }'s staff and board list.`,
};

// ── affiliations ─────────────────────────────────────────────────────────────

const RelationshipFields = ({ form, readOnly }: { form: any; readOnly: boolean }) => (
  <>
    <Col md={6}>
      <TextField
        form={form}
        name="natureOfRelationship"
        label="Nature of relationship"
        readOnly={readOnly}
        placeholder="For example Owner, Shareholder, Relative"
      />
    </Col>
    <Col md={6}>
      <TextField
        form={form}
        name="informationSource"
        label="Information source"
        readOnly={readOnly}
        placeholder="Where this was learned"
      />
    </Col>
    <Col md={12}>
      <SwitchField
        form={form}
        name="relationshipConfirmed"
        label="Relationship confirmed"
        readOnly={readOnly}
      />
    </Col>
  </>
);

const relationshipMeta = (record: any) =>
  joinMeta(record?.natureOfRelationship, record?.relationshipConfirmed ? "Confirmed" : "Not confirmed");

const relationshipValues = (record: any) => ({
  natureOfRelationship: record?.natureOfRelationship || "",
  informationSource: record?.informationSource || "",
  relationshipConfirmed: !!record?.relationshipConfirmed,
});

const relationshipPayload = (values: Record<string, any>) => ({
  natureOfRelationship: values.natureOfRelationship,
  informationSource: values.informationSource,
  relationshipConfirmed: !!values.relationshipConfirmed,
});

const ContractorAffiliationFields = ({ form, readOnly }: FieldsProps) => (
  <Row>
    <Col md={12}>
      <AsyncSelectField
        form={form}
        name="contractor"
        label="Contractor"
        required
        readOnly={readOnly}
        placeholder="Search contractors by name"
        loadOptions={loadContractors()}
        hint="The affiliation also appears on the contractor's profile."
      />
    </Col>
    <RelationshipFields form={form} readOnly={readOnly} />
  </Row>
);

export const CONTRACTOR_AFFILIATIONS: RecordKindConfig = {
  kind: "contractor-affiliations",
  title: "Affiliations to contractors",
  noun: "affiliation",
  permissions: profileRecordPermissions(
    "addContractorAffiliation",
    "editContractorAffiliation",
    "removeContractorAffiliation"
  ),
  store: personRecordStore("contractor-affiliations"),
  records: (person) =>
    [...(person?.contractorAffiliations || [])].sort((a, b) =>
      (a?.contractor?.contractorName || "").localeCompare(b?.contractor?.contractorName || "")
    ),
  summary: (record) => ({
    title: record?.contractor?.contractorName || "Unknown contractor",
    meta: relationshipMeta(record),
    avatar: { icon: "ri-building-line", tone: "primary" },
  }),
  profileLink: (record) =>
    record?.contractor?.id
      ? { to: `/contractors/${record.contractor.id}`, label: "Open contractor" }
      : null,
  emptyValues: { contractor: null, ...relationshipValues(null) },
  toValues: (record) => ({
    contractor: option(record?.contractor?.id, record?.contractor?.contractorName),
    ...relationshipValues(record),
  }),
  toPayload: (values) => ({
    contractor: values.contractor?.value ?? null,
    ...relationshipPayload(values),
  }),
  schema: Yup.object({
    contractor: Yup.object().nullable().required("Choose a contractor"),
  }),
  Fields: ContractorAffiliationFields,
  removeNote: (record) =>
    `This deletes the affiliation here and on ${
      record?.contractor?.contractorName || "the contractor"
    }'s profile. The contractor itself stays in the register.`,
};

const PersonAffiliationFields = ({ form, readOnly, ownerId }: FieldsProps) => (
  <Row>
    <Col md={12}>
      <AsyncSelectField
        form={form}
        name="affiliatedTo"
        label="Affiliated person"
        required
        readOnly={readOnly}
        placeholder="Search people by name"
        loadOptions={loadPersons(ownerId)}
      />
    </Col>
    <RelationshipFields form={form} readOnly={readOnly} />
  </Row>
);

export const PERSON_AFFILIATIONS: RecordKindConfig = {
  kind: "person-affiliations",
  title: "Affiliations to persons",
  noun: "affiliation",
  permissions: profileRecordPermissions(
    "addPersonAffiliation",
    "editPersonAffiliation",
    "removePersonAffiliation"
  ),
  store: personRecordStore("person-affiliations"),
  records: (person) =>
    [...(person?.personAffiliations || [])].sort((a, b) =>
      (a?.affiliatedTo?.fullName || "").localeCompare(b?.affiliatedTo?.fullName || "")
    ),
  summary: (record) => ({
    title: record?.affiliatedTo?.fullName || "Unnamed person",
    meta: relationshipMeta(record),
    avatar: { initials: initials(record?.affiliatedTo?.fullName), tone: "primary" },
  }),
  profileLink: (record) =>
    record?.affiliatedTo?.id
      ? { to: `/persons/${record.affiliatedTo.id}`, label: "Open person" }
      : null,
  emptyValues: { affiliatedTo: null, ...relationshipValues(null) },
  toValues: (record) => ({
    affiliatedTo: option(record?.affiliatedTo?.id, record?.affiliatedTo?.fullName),
    ...relationshipValues(record),
  }),
  toPayload: (values) => ({
    affiliatedTo: values.affiliatedTo?.value ?? null,
    ...relationshipPayload(values),
  }),
  schema: Yup.object({
    affiliatedTo: Yup.object().nullable().required("Choose a person"),
  }),
  Fields: PersonAffiliationFields,
  removeNote: (record) =>
    `This deletes the affiliation only. ${
      record?.affiliatedTo?.fullName || "The other person"
    }'s own record stays in the register.`,
  footnote: (person) => {
    const count = person?.personAffiliatedTo?.length || 0;
    if (!count) return null;
    return count === 1
      ? "1 more affiliation was recorded on the other person's profile and is edited there."
      : `${count} more affiliations were recorded on other persons' profiles and are edited there.`;
  },
};

// ── SEMG mentions ────────────────────────────────────────────────────────────

/** The same form and list as on contractors, saved under the person. */
export const SEMG_MENTIONS: RecordKindConfig = {
  ...CONTRACTOR_SEMG_MENTIONS,
  permissions: profileRecordPermissions("addSEMGMention", "editSEMGMention", "removeSEMGMention"),
  store: personRecordStore("semg-mentions"),
  removeNote: () =>
    "This deletes the mention from the person. The SEMG report itself is not affected.",
};
